import{ getDatabase, ref, onValue } from 'firebase/database';

import { Injectable } from '@angular/core';
import { getAuth, onAuthStateChanged, signOut, GoogleAuthProvider, signInWithPopup, getAdditionalUserInfo, getIdToken, createUserWithEmailAndPassword, sendEmailVerification, signInWithEmailAndPassword, sendPasswordResetEmail, confirmPasswordReset, Auth, User } from 'firebase/auth';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Observable, Subject } from 'rxjs';
import { Router } from '@angular/router';

import { AppConst } from './app-const';
import { ConfigurationService } from './configuration.service';

import { Session } from './session';
import { FireUser } from './firestore-models/user';
import { TranslateService } from '@ngx-translate/core';


@Injectable({
  providedIn: 'root'
})
export class SessionService {

  public session = new Session();
  public sessionSubject = new Subject<Session>();
  public sessionState = this.sessionSubject.asObservable();

  auth!: Auth;
  user!: User;
  unsubscribe: any;
  public authObservable!: Observable<Session>;
  public currentLanguage!: string;

  constructor(
    private router: Router,
    private angularFirestore: AngularFirestore,
    private translate: TranslateService

  ) {
    this.auth = getAuth();
    this.subscribeUser();
    this.currentLanguage = this.session.current_language;

  }

  private subscribeUser(): void {
    const userObservable = new Observable<User | null>(observer => {
      const unsubscribe = onAuthStateChanged(this.auth, observer);
      return () => {
        if (unsubscribe !== null) {
          unsubscribe();
        }
      };
    });
    this.authObservable = new Observable<Session>(authObserver => {
      userObservable.subscribe(user => {
        this.session.authUser = user;
        if (!!user) {
          this.auth.currentUser?.getIdTokenResult()
          .then((idTokenResult) => {
            if (idTokenResult.claims['firestoreAuth']) {
              this.session.uid = user.email ? user.email : user.providerData[0].email;
              const subUsermain = this.angularFirestore.collection<FireUser>(
                'users', ref => ref.where('user_id', '==', this.session.uid))
                .valueChanges({ idField: 'id' }).subscribe(docs => {
                if (!!docs && docs.length === 1) {
                  this.session.isLogin = true;
                  this.session.user = docs[0];
                } else {
                  this.session.isLogin = false;
                  this.session.user = null;
                  this.session.token = null;
                }
                subUsermain.unsubscribe();
                this.sessionSubject.next(this.session);
                authObserver.next(this.session);
              });
            } else {
              console.warn('not found custom claim. user : ', user.uid);
              this.logout();
            }
          })
          .catch((error) => {
            console.error('getIdTokenResult error : ', error);
            this.logout();
          });
 
        } else {
          this.session.isLogin = false;
          this.session.user = null;
          this.session.uid = null;
          this.session.token = null;
          this.sessionSubject.next(this.session);
          authObserver.next(this.session);
        }
      });
    });
  }

  async getIdToken(): Promise<string> {
    // return !!this.session.token ? this.session.token : '';
    let _token = '';
    await getIdToken(this.session.authUser!).then(token => {
      _token = token;
    });
    return _token;
  }

  signup(admin_id: string, password: string): void {
    createUserWithEmailAndPassword(this.auth, admin_id, password)
      .then((userCredential) => {
        sendEmailVerification(userCredential.user);
        this.sessionSubject.next(this.session.reset());
        alert(this.translate.instant('メールアドレス確認メールを送信しました。'))
		  })
		  .catch((error) => {
        alert(this.translate.instant('アカウントの作成に失敗しました。') + '\n' + error)
		  });
  }

  loginEmail(admin_id: string, password: string): void {
    signInWithEmailAndPassword(this.auth, admin_id, password)
      .then((userCredential) => {
        return this.router.navigate([AppConst.ROUTING_LINK_TOP]);
      })
      .catch((error) => {
        console.error(error);;
      });
  }

  loginGoogle(): void {
    const provider = new GoogleAuthProvider();
    provider.addScope('profile');
    provider.addScope('email');
    signInWithPopup(this.auth,provider)
    .then((userCredential) => {
      // 初回ログインか判定するため、新規ユーザーフラグを取得
      const isNewUser = getAdditionalUserInfo(userCredential)?.isNewUser;
      // ２回目以降ログイン
      if (!isNewUser) {
        return this.router.navigate([AppConst.ROUTING_LINK_TOP]);
      // 初回ログイン（FirebaseAuthentication登録時）
      } else {
        // FirebaseAuthentication登録後にFunctionでCustomClaimsの追加登録がされたら（realtimeDatabase更新がトリガ）
        // Authenticationのトークンをリフレッシュして最新のAuthセッション情報を取得する（最新にCustomClaimsが含まれる）
        let metadataRef: any = null;
        this.auth.onAuthStateChanged(user => {
          if (!!user) {
            const db = getDatabase();
            metadataRef = ref(db, 'metadata/' + user.uid + '/refreshTime');
            onValue(metadataRef, (snapshot) => {
              const data = snapshot.val();
              if (data) {
                user.getIdToken(true)
                .then((token) => {
                  return this.router.navigate([AppConst.ROUTING_LINK_TOP]);
                });
              }
              return;
            });
          }
        });
        return;
      }
    })
    .catch(error => {
      console.log(error);
    });
  }

  logout(): void {
    signOut(this.auth)
      .then(() => {
        this.sessionSubject.next(this.session.reset());
        return this.router.navigate([AppConst.ROUTING_LINK_SIGN_IN]);
      })
      .catch((error) => {
        console.error(error);;
      })
  }

  // ログイン状況確認(State)
  checkLoginState(): Observable<Session> {
    return new Observable<Session>(observer => {
      observer.next(this.session);
    });
  }

  passwordResetMail(admin_id: string): void {
    sendPasswordResetEmail(this.auth, admin_id)
      .then((res) => {
        this.sessionSubject.next(this.session.reset());
        alert(this.translate.instant('パスワード再設定メールを送信しました。'))
		  })
		  .catch((error) => {
        alert(this.translate.instant('パスワード再設定メールの送信に失敗しました。') + '\n' + error)
		  });
  }

  passwordReset(oob_code: string, password: string): void {
    confirmPasswordReset(this.auth, oob_code, password)
    .then(() => {
      this.sessionSubject.next(this.session.reset());
      alert(this.translate.instant('パスワードを更新しました。'));
      return this.router.navigate([AppConst.ROUTING_LINK_SIGN_IN]);
    })
    .catch((error) => {
      alert(this.translate.instant('パスワードの更新に失敗しました。') + '\n' + error)
    });
  }
  // #216 管理画面の言語をsessionに保存
  setCurrentLanguage(current_language: string): void {
    this.session.current_language = current_language;

    // localStorageに保存
    localStorage.setItem('current_language', current_language);
  }

}
